export interface ICropArea {
	height: number;
	width: number;
	x: number;
	y: number;
}

export interface ICropImageOptions {
	/**
	 * Forces the output to exactly this size. Without it the crop keeps its own pixels, capped by
	 * {@link maxOutputEdge}. Set it only where the consumer needs one fixed size, as the testimonial
	 * background does.
	 */
	outputSize?: { height: number; width: number };
	/** Longest edge of the output, in pixels. The crop is never enlarged past its own size to reach it. */
	maxOutputEdge?: number;
	/** JPEG/WebP quality. Ignored for PNG, which is lossless. */
	quality?: number;
}

/**
 * A crop never gains detail, so a 400px selection written out at 1080px is a bigger file carrying the
 * same picture. The cap only ever shrinks.
 */
const DEFAULT_MAX_OUTPUT_EDGE = 2048;
const DEFAULT_QUALITY = 0.92;

/** Formats a canvas can write back. Anything else is written as PNG, which is lossless and universal. */
const ENCODABLE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

/**
 * A JPEG re-encoded as PNG routinely grows several times over — a photograph has no flat regions for
 * PNG to pack — so the output keeps the input's format wherever a canvas can write it.
 */
const resolveOutputType = (sourceType: string | undefined): string => {
	const normalized = sourceType?.toLowerCase().split(';')[0].trim() ?? '';
	return ENCODABLE_TYPES.has(normalized) ? normalized : 'image/png';
};

const replaceExtension = (fileName: string, outputType: string): string => {
	const extension = outputType === 'image/jpeg' ? 'jpg' : outputType === 'image/webp' ? 'webp' : 'png';
	const base = fileName.replace(/\.[^.]+$/, '') || 'image';
	return `${base}.${extension}`;
};

const loadImage = (imageUrl: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const image = new Image();
		image.onload = () => resolve(image);
		image.onerror = () => reject(new Error('Failed to load image for cropping'));
		image.src = imageUrl;
	});

/**
 * The output size for a crop, honouring an exact request, then the cap, then the crop's own pixels.
 * Kept whole-pixel: a fractional canvas dimension is silently truncated by the browser, which shifts
 * the aspect ratio away from the one the user framed.
 */
const resolveOutputSize = (cropArea: ICropArea, options: ICropImageOptions) => {
	if (options.outputSize) {
		return {
			height: Math.max(1, Math.round(options.outputSize.height)),
			width: Math.max(1, Math.round(options.outputSize.width)),
		};
	}

	const maxEdge = options.maxOutputEdge ?? DEFAULT_MAX_OUTPUT_EDGE;
	const longestEdge = Math.max(cropArea.width, cropArea.height);
	const scale = longestEdge > maxEdge ? maxEdge / longestEdge : 1;
	return {
		height: Math.max(1, Math.round(cropArea.height * scale)),
		width: Math.max(1, Math.round(cropArea.width * scale)),
	};
};

/**
 * Produces a cropped image file from a source URL and a pixel crop area in the source's own
 * coordinates, as react-easy-crop reports it.
 *
 * `imageUrl` must be a data URL or a same-origin blob URL. A cross-origin image taints the canvas and
 * `toBlob` then throws a SecurityError, so resolve a remote attachment with `useGetImageUrl` first.
 */
export const cropImage = async (
	imageUrl: string,
	cropArea: ICropArea,
	fileName = 'cropped-image.png',
	options: ICropImageOptions = {}
): Promise<File> => {
	const image = await loadImage(imageUrl);

	// react-easy-crop can report a selection a pixel or two past the edge when the image is zoomed
	// out to fit; drawImage reads those as transparent, which shows as a seam along the crop.
	const sourceX = Math.max(0, Math.min(cropArea.x, image.naturalWidth));
	const sourceY = Math.max(0, Math.min(cropArea.y, image.naturalHeight));
	const sourceWidth = Math.max(1, Math.min(cropArea.width, image.naturalWidth - sourceX));
	const sourceHeight = Math.max(1, Math.min(cropArea.height, image.naturalHeight - sourceY));

	const outputSize = resolveOutputSize({ height: sourceHeight, width: sourceWidth, x: sourceX, y: sourceY }, options);

	const canvas = document.createElement('canvas');
	canvas.width = outputSize.width;
	canvas.height = outputSize.height;

	const context = canvas.getContext('2d');
	if (!context) {
		throw new Error('Could not get canvas context');
	}

	const outputType = resolveOutputType(image.src.startsWith('data:') ? image.src.slice(5).split(';')[0] : undefined);

	// A JPEG has no alpha channel, so an un-painted canvas encodes its transparent pixels as black.
	if (outputType === 'image/jpeg') {
		context.fillStyle = '#ffffff';
		context.fillRect(0, 0, canvas.width, canvas.height);
	}

	context.imageSmoothingQuality = 'high';
	context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, outputSize.width, outputSize.height);

	const blob = await new Promise<Blob | null>(resolve =>
		canvas.toBlob(resolve, outputType, options.quality ?? DEFAULT_QUALITY)
	);
	if (!blob) {
		throw new Error('Failed to create blob from canvas');
	}

	return new File([blob], replaceExtension(fileName, outputType), { type: outputType });
};
